import { useEffect, useState } from "react";
import { Bell, Loader2, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { dateTimeIt, money } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const NotificationBell = () => {
  const { t } = useI18n();
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = () => api.get("/notifications").then(({ data }) => setData(data)).catch(() => {});

  useEffect(() => {
    load();
    const id = setInterval(load, 120000);
    return () => clearInterval(id);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      await api.post("/notifications/read");
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      await api.post("/notifications/run-check");
      await load();
      toast.success(t("notif.checked"));
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="relative">
      <button
        data-testid="notification-bell"
        onClick={toggle}
        className="relative rounded-md border border-slate-200 bg-white p-2 text-slate-600 transition-colors duration-200 hover:text-slate-900"
      >
        <Bell className="h-4 w-4" />
        {data.unread > 0 && (
          <span
            data-testid="notification-count"
            className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white"
          >
            {data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 z-40 mt-2 w-80 rounded-lg border border-slate-200 bg-white p-3 shadow-lg"
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">{t("notif.title")}</p>
            <button
              data-testid="notification-check"
              onClick={checkNow}
              className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"
            >
              {checking ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              {t("notif.checkNow")}
            </button>
          </div>
          <ul className="scroll-thin mt-3 max-h-72 space-y-2 overflow-y-auto">
            {data.items.length === 0 && (
              <li className="py-6 text-center text-sm text-slate-500">{t("notif.empty")}</li>
            )}
            {data.items.map((n) => (
              <li key={n.id} className="rounded-md border border-slate-100 bg-slate-50 p-2.5">
                <Link to="/solleciti" onClick={() => setOpen(false)} className="block">
                  <p className="text-sm font-medium">
                    {n.meta?.kind === "overdue"
                      ? `${n.meta.number} ${t("notif.overdueBy")} ${n.meta.days} ${t("notif.days")}`
                      : n.title}
                  </p>
                  <p className="text-xs text-slate-500">
                    {n.meta?.kind === "overdue"
                      ? `${n.meta.client} · ${money(n.meta.amount, n.meta.currency)}`
                      : n.detail}
                  </p>
                  <p className="font-mono-num text-[11px] text-slate-400">{dateTimeIt(n.created_at)}</p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
